// approver/approval.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Snackbar, TextField, Chip, CircularProgress, Paper, Alert
} from '@material-ui/core';
import moment from 'moment';
import { makeStyles } from '@material-ui/styles';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Layers as LayersIcon,
  Search as SearchIcon
} from '@material-ui/icons';
import approverApi from '../../../api/approverApi';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
    marginTop: '80px',
    marginRight: '24px',
    transition: 'all 0.3s ease',
  },
  paper: {
    backgroundColor: '#1a2235',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    border: '1px solid #2d3748'
  },
  card: {
    backgroundColor: '#1e293b',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    color: '#fff',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#2d3748'
    }
  },
  chip: {
    margin: '4px',
    backgroundColor: 'rgba(6, 182, 212, 0.1)',
    color: '#06b6d4',
    borderColor: '#06b6d4',
    border: '1px solid'
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2235',
      color: '#fff',
      borderRadius: theme.spacing(2),
    },
  },
  successButton: {
    background: '#22c55e',
    color: '#fff',
    '&:hover': {
      background: '#16a34a'
    }
  },
  errorButton: {
    background: '#ef4444',
    color: '#fff',
    '&:hover': {
      background: '#dc2626'
    }
  }
}));

export default function CourtApprovalPage() {
  const classes = useStyles();
  const { isCollapse } = useCollapseDrawer();
  const [pendingCourts, setPendingCourts] = useState([]);
  const [filteredCourts, setFilteredCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const containerStyle = {
    marginLeft: isCollapse ? `126px` : `304px`
  };
  if (window.innerWidth <= 1024) containerStyle.marginLeft = '24px';

  const convertTo12Hour = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const h = parseInt(hours);
    const m = parseInt(minutes);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${suffix}`;
  };

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const res = await approverApi.get_pending_courts();
      const courts = res?.data?.data || [];
      setPendingCourts(courts);
      setFilteredCourts(courts);
    } catch (err) {
      console.error(err);
      setError('Failed to load courts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourts();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = pendingCourts.filter(c =>
      c.court_name.toLowerCase().includes(query) ||
      c.location?.toLowerCase().includes(query)
    );
    setFilteredCourts(filtered);
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await approverApi.approve_court(selectedCourt.id);
      setSuccessMessage('Court approved.');
      setOpenApproveDialog(false);
      fetchCourts();
    } catch (err) {
      setError('Failed to approve court.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!adminNotes) {
      setError('Please provide a rejection reason.');
      return;
    }
    try {
      setActionLoading(true);
      await approverApi.reject_court(selectedCourt.id, { reason: adminNotes });
      setSuccessMessage('Court rejected.');
      setOpenRejectDialog(false);
      fetchCourts();
    } catch (err) {
      setError('Failed to reject court.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box className={classes.container} style={containerStyle}>
      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage('')}>
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>

      <Paper className={classes.paper}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search courts by name or location"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <SearchIcon style={{ color: '#06b6d4', marginRight: 8 }} />
          }}
          style={{ color: '#fff' }}
        />
      </Paper>

      <Grid container spacing={3}>
        {filteredCourts.map((court, index) => (
          <Grid item xs={12} md={6} lg={4} key={court.id}>
            <Card className={classes.card} onClick={() => { setSelectedCourt(court); setOpenDialog(true); }}>
              <Typography variant="h6" gutterBottom>{court.court_name}</Typography>
              <Typography variant="body2"><LocationIcon fontSize="small" /> {court.location || 'N/A'}</Typography>
              <Typography variant="body2"><CategoryIcon fontSize="small" /> {court.description || 'N/A'}</Typography>
              <Typography variant="body2"><LayersIcon fontSize="small" /> Capacity: {court.hourly_rate || 'N/A'}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detail Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} classes={{ paper: classes.dialog }} maxWidth="md" fullWidth>
        {selectedCourt && (
          <>
            <DialogTitle>
              Court: {selectedCourt.court_name}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={4}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8' }}>Court Name:</Typography>
                      <Typography>{selectedCourt.court_name}</Typography>
                    </div>

                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8' }}>Description:</Typography>
                      <Typography>{selectedCourt.description || 'N/A'}</Typography>
                    </div>

                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8' }}>Location:</Typography>
                      <Typography>{selectedCourt.location || 'N/A'}</Typography>
                    </div>

                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8' }}>Hourly Rate:</Typography>
                      <Typography>₱{parseFloat(selectedCourt.hourly_rate).toFixed(2)}</Typography>
                    </div>

                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8' }}>Max Capacity:</Typography>
                      <Typography>{selectedCourt.max_capacity || 'N/A'} persons</Typography>
                    </div>

                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8' }}>Schedule:</Typography>
                      <Typography>
                        {selectedCourt.opening_time && selectedCourt.closing_time
                          ? `${convertTo12Hour(selectedCourt.opening_time)} - ${convertTo12Hour(selectedCourt.closing_time)}`
                          : 'N/A'}
                      </Typography>
                    </div>

                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8' }}>Created At:</Typography>
                      <Typography>
                        {selectedCourt.created_at
                          ? moment(selectedCourt.created_at).utcOffset('+08:00').format('MMMM D, YYYY [at] h:mm A')
                          : 'N/A'}
                      </Typography>
                    </div>
                  </Box>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8', marginBottom: 8 }}>Features:</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {selectedCourt.features
                        ? Object.entries(selectedCourt.features).map(([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              className={classes.chip}
                              size="small"
                            />
                          ))
                        : <Typography variant="body2">None</Typography>}
                    </Box>
                  </Box>

                  <Box mt={3}>
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#94a3b8', marginBottom: 8 }}>Images:</Typography>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      {selectedCourt.images && selectedCourt.images.length > 0
                        ? selectedCourt.images.map((img, i) => (
                            <img
                              key={i}
                              src={img.startsWith('http') ? img : `http://localhost:7002/${img}`}
                              alt={`court-img-${i}`}
                              style={{
                                height: 100,
                                width: 160,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #2d3748',
                                cursor: 'pointer'
                              }}
                              onClick={() => setImagePreviewUrl(img.startsWith('http') ? img : `http://localhost:7002/${img}`)}
                            />
                          ))
                        : <Typography variant="body2">No images provided.</Typography>}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setOpenDialog(false); setOpenRejectDialog(true); }} className={classes.errorButton} startIcon={<RejectIcon />}>Reject</Button>
              <Button onClick={() => { setOpenDialog(false); setOpenApproveDialog(true); }} className={classes.successButton} startIcon={<ApproveIcon />}>Approve</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)} classes={{ paper: classes.dialog }}>
        <DialogTitle><ApproveIcon /> Confirm Approval</DialogTitle>
        <DialogContent>
          <Alert severity="success">Are you sure you want to approve this court?</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button onClick={handleApprove} className={classes.successButton} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} classes={{ paper: classes.dialog }}>
        <DialogTitle><RejectIcon /> Reject Court</DialogTitle>
        <DialogContent>
          <Alert severity="warning">Provide a reason for rejecting this court.</Alert>
          <TextField
            fullWidth
            label="Reason"
            multiline
            rows={3}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            variant="outlined"
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleReject} className={classes.errorButton} disabled={actionLoading || !adminNotes}>
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!imagePreviewUrl} onClose={() => setImagePreviewUrl(null)} maxWidth="md" fullWidth>
        <Box
          style={{
            backgroundColor: '#000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 24
          }}
        >
          <img
            src={imagePreviewUrl}
            alt="Preview"
            style={{
              maxHeight: '80vh',
              width: '100%',
              objectFit: 'contain',
              borderRadius: 12,
              boxShadow: '0 0 24px rgba(255, 255, 255, 0.1)'
            }}
            onClick={() => setImagePreviewUrl(null)}
          />
        </Box>
      </Dialog>
    </Box>
  );
}
